import { Check } from "lucide-react";

// [CONFIRM] Validate dollar ranges and engagement durations against current pricing
// before publishing externally. Ranges are deliberately conservative and shaped
// to qualify leads, not quote them.
const shapes = [
  {
    label: "Project",
    name: "Scoped Engagement",
    outcome:
      "Close a specific risk. Walk away with a deliverable your board, auditor, and insurance carrier can act on.",
    duration: "2–6 weeks",
    range: "$25k–$120k",
    blurb:
      "Fixed-scope assessment, remediation, and validation. Closes a specific risk: a stalled enterprise deal, a launch on a clinical timeline, an OCR signal, or a SOC 2 gap.",
    includes: [
      "Findings report with regulatory citations",
      "Remediation roadmap with assigned owners",
      "Validation log per Critical and High finding",
      "Evidence package for auditor / customer / carrier",
    ],
    primary: false,
  },
  {
    label: "Retainer",
    name: "Continuous Compliance Program",
    outcome:
      "The ongoing program that keeps your compliance posture current and documented.",
    duration: "Quarterly · annual minimum",
    range: "$8k–$25k / month",
    blurb:
      "The ongoing program your board, auditor, and cyber insurance carrier expect to see evidence of. Designed to renew and to compound — the customer of record stays the firm, not a person.",
    includes: [
      "Monthly security posture report",
      "Quarterly compliance re-assessment",
      "Regulatory change monitoring and advisory",
      "Named engagement lead with documented backup",
      "Priority response within one business day",
    ],
    primary: true,
  },
  {
    label: "Advisory",
    name: "Fractional Security & Privacy Advisor",
    outcome:
      "A documented security function without the cost of a full-time hire.",
    duration: "Monthly · rolling",
    range: "$4k–$10k / month",
    blurb:
      "Embedded advisory for teams that need a documented security and privacy function but are not yet ready to hire one. Sits across engineering, legal, and the executive team.",
    includes: [
      "Standing weekly office hours",
      "Customer security review support",
      "Vendor and BAA review",
      "Audit and certification preparation",
    ],
    primary: false,
  },
];

export default function ArqEngagementShapes() {
  return (
    <section id="engagements" className="bg-arq-surface1/40 border-y border-arq-border py-20 md:py-[120px] px-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-center max-w-[820px] mx-auto mb-14">
          <div className="arq-label mb-6">How We Engage</div>
          <h2
            className="font-display font-semibold text-arq-text mb-5"
            style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}
          >
            Three engagement shapes. Written proposal within 48 hours of a discovery call.
          </h2>
          <p className="text-arq-muted text-[17px] leading-[1.75]">
            Most clients start with a scoped engagement and convert to a retainer once the
            initial risk is closed. The retainer is the program your auditor, board, and
            cyber insurance carrier expect to see evidence of every quarter.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {shapes.map((s) => (
            <div
              key={s.name}
              className={`arq-card p-7 md:p-8 flex flex-col h-full ${
                s.primary ? "border-arq-blue/60 ring-1 ring-arq-blue/30" : ""
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono-tech text-[11px] tracking-[0.12em] uppercase text-arq-blue">
                  {s.label}
                </div>
                {s.primary && (
                  <span className="font-mono text-[10px] tracking-wider uppercase bg-arq-blue/10 border border-arq-blue/30 text-arq-blue rounded-full px-2.5 py-0.5">
                    Most clients
                  </span>
                )}
              </div>
              <h3 className="font-display text-[20px] font-semibold text-arq-text mb-3 leading-tight">
                {s.name}
              </h3>
              <p className="text-arq-text/90 text-[15px] leading-[1.6] mb-3">
                {s.outcome}
              </p>
              <div className="font-mono text-[12px] text-arq-tertiary mb-4">
                {s.duration} · <span className="text-arq-muted">{s.range}</span>
              </div>
              <p className="text-arq-muted text-[14px] leading-[1.7] mb-5">{s.blurb}</p>
              <ul className="space-y-2.5 mb-6 flex-1">
                {s.includes.map((it) => (
                  <li
                    key={it}
                    className="flex items-start gap-2.5 text-[14px] text-arq-text/90 leading-snug"
                  >
                    <Check
                      className="w-4 h-4 mt-0.5 flex-shrink-0"
                      style={{ color: "hsl(var(--arq-teal))" }}
                      aria-hidden
                    />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
              <a
                href="/#contact"
                className={`mt-auto inline-flex items-center justify-center font-semibold text-[14px] px-5 py-3 rounded-md transition-colors ${
                  s.primary
                    ? "bg-arq-blue text-white hover:bg-arq-blue/90"
                    : "border border-arq-border text-arq-text hover:border-arq-blue hover:text-arq-blue"
                }`}
              >
                Discuss this engagement
              </a>
            </div>
          ))}
        </div>

        <p className="text-arq-tertiary text-[12px] text-center mt-8 max-w-[820px] mx-auto leading-relaxed">
          Ranges are indicative and shaped to qualify scope. Final pricing is fixed in writing
          after a discovery call and never billed against an open hourly clock.
        </p>
      </div>
    </section>
  );
}
